/**
 * Identity targets for Lockbox → native bridge.
 *
 * ## Apple enforcement invariant (iOS)
 * - **ManagedSettings** shields require `ApplicationToken` (FamilyControls). You cannot enforce with bundleId alone.
 * - **ApplicationToken must never cross into JS** — only opaque refs (`application_token_ref`) or lockbox ids.
 * - **`bundle_hint`** is for UX / JS simulator / catalog only — **never** pass to `shield.applications`.
 *
 * @typedef {Object} RestrictionTarget
 * @property {string} id lockbox inventory uuid, opaque ref string, or reverse-DNS hint
 * @property {'lockbox_app'|'application_token_ref'|'bundle_hint'|'android_package'} type
 */

/**
 * @param {import('./types.js').LockboxAppEntity|null|undefined} app
 * @param {string} lockboxAppId
 * @returns {RestrictionTarget[]}
 */
/**
 * Android executor: `packageName` (preferred) or legacy `bundleId` on the row.
 * @param {import('./types.js').LockboxAppEntity|null|undefined} app
 */
export function expandAndroidPackagesFromRow(app) {
  const raw = app?.packageName || app?.bundleId;
  if (!raw || !String(raw).trim()) return [];
  return [{ id: normalizePackageName(raw), type: "android_package" }];
}

export function expandEnforceableTargetsFromRow(app, lockboxAppId) {
  /** @type {RestrictionTarget[]} */
  const out = [];
  out.push({ id: lockboxAppId, type: "lockbox_app" });

  const opaque = app?.nativeRef?.opaqueKey;
  if (opaque && String(opaque).trim()) {
    out.push({ id: String(opaque).trim(), type: "application_token_ref" });
  }
  for (const t of expandAndroidPackagesFromRow(app)) {
    out.push(t);
  }
  return dedupeTargets(out);
}

/** @param {string} raw */
export function normalizePackageName(raw) {
  return normalizeBundleId(raw);
}

/**
 * Optional bundle id for **non-enforcement** paths (tests, UI labels, future catalog).
 * @param {import('./types.js').LockboxAppEntity|null|undefined} app
 * @returns {RestrictionTarget[]}
 */
export function expandBundleHintsFromRow(app) {
  if (!app?.bundleId || !String(app.bundleId).trim()) return [];
  return [{ id: normalizeBundleId(app.bundleId), type: "bundle_hint" }];
}

/**
 * @param {RestrictionTarget[]} list
 * @returns {RestrictionTarget[]}
 */
function dedupeTargets(list) {
  const m = new Map();
  for (const t of list) {
    if (!t?.id || !t?.type) continue;
    const key = `${t.type}\n${String(t.id).trim()}`;
    if (!m.has(key)) m.set(key, { id: String(t.id).trim(), type: t.type });
  }
  return Array.from(m.values());
}

/**
 * @param {string} raw
 */
export function normalizeBundleId(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase();
}

/**
 * Only targets the Swift bridge may use to build `Set<ApplicationToken>` (after DB lookup / decode).
 * @param {string[]} lockboxAppIds
 * @param {import('./types.js').LockboxAppEntity[]} inventory
 * @returns {RestrictionTarget[]}
 */
export function restrictionEnforceableTargetsForAppIds(lockboxAppIds, inventory) {
  const byId = new Map(inventory.map((a) => [a.id, a]));
  /** @type {Map<string, RestrictionTarget>} */
  const out = new Map();
  for (const appId of lockboxAppIds) {
    const app = byId.get(appId);
    for (const t of expandEnforceableTargetsFromRow(app, appId)) {
      out.set(`${t.type}\n${t.id}`, t);
    }
  }
  return Array.from(out.values()).sort((a, b) => a.type.localeCompare(b.type) || a.id.localeCompare(b.id));
}

/**
 * Bundle hints for blocked/allowed rows (JS simulator / UX only).
 * @param {string[]} lockboxAppIds
 * @param {import('./types.js').LockboxAppEntity[]} inventory
 * @returns {RestrictionTarget[]}
 */
export function restrictionBundleHintsForAppIds(lockboxAppIds, inventory) {
  const byId = new Map(inventory.map((a) => [a.id, a]));
  const out = new Map();
  for (const appId of lockboxAppIds) {
    const app = byId.get(appId);
    for (const t of expandBundleHintsFromRow(app)) {
      out.set(`${t.type}\n${t.id}`, t);
    }
  }
  return Array.from(out.values()).sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * @deprecated Use {@link expandEnforceableTargetsFromRow} — returns **enforceable** targets only (no bundle hints).
 * @param {import('./types.js').LockboxAppEntity|null|undefined} app
 * @param {string} lockboxAppId
 */
export function expandInventoryRowToRestrictionTargets(app, lockboxAppId) {
  return expandEnforceableTargetsFromRow(app, lockboxAppId);
}

/**
 * @deprecated Use {@link restrictionEnforceableTargetsForAppIds}
 */
export function restrictionTargetsForAppIds(lockboxAppIds, inventory) {
  return restrictionEnforceableTargetsForAppIds(lockboxAppIds, inventory);
}
